import { FC, useEffect, useState } from 'react'
import produce from 'immer'
import {
  Input,
  InputChangeEvent,
  NumericTextBox,
  NumericTextBoxChangeEvent,
  TextArea,
  TextAreaChangeEvent
} from '@progress/kendo-react-inputs'
import { Label } from '@progress/kendo-react-labels'
import { StackLayout } from '@progress/kendo-react-layout'

import hearingMessage from '../../../data/hearing-message.json'
import { ErrorMessage, ErrorMessages, SupplierHearingHeader } from '../../../components/parts'
import { HearingSheetFooter } from './HearingSheetFooter'
import { useAccessibleAuthority, useLeaveGuard, usePageTitle } from '../../../components/hooks'
import { GetSupplierHearingSheetItemRes } from '@gateway.Supplier'
import { Typography } from '@progress/kendo-react-common'
import { useAppSelector, useAppDispatch, CommonActions } from '../../../stores'
import { HearingSheetItemGateway } from '../../../gateway/supplier/HearingSheetItemGateway'
import { Authority } from '../../../gateway/system/Authority'

const INIT_HEARING_SHEET_DATA: GetSupplierHearingSheetItemRes = {
  simulationYm: '',
  supplierCd: '',
  supplierBranchCd: '',
  factoryCd: '',
  expenseCd: '',
  addStsCd: '',
  addSts: '',
  replyNmLast: '',
  replyNmFirst: '',
  ourCompanyOccupancy: 0,
  marketTrend: '',
  yamadumiSts: '',
  newBuildingSts: '',
  problem: '',
  updDtm: ''
}

export const HearingSheet: FC = () => {
  useAccessibleAuthority(Authority.Builder, Authority.Material, Authority.Branch, Authority.Supplier)
  usePageTitle('ヒアリングシート')
  const dispatch = useAppDispatch()

  const { regStatus } = useAppSelector((state) => state.confirmreply)

  const [isChanged, setIsChanged] = useState(false)
  const [hearingSheetVal, setHearingSheetVal] = useState(
    INIT_HEARING_SHEET_DATA
  )
  const [notificationMessages, setNotificationMessages] = useState<ErrorMessage[]>([])
  const [errorMessages, setErrorMessages] = useState<ErrorMessage[]>([])
  const {
    simulationYm,
    supplierCd,
    factoryCd,
    expenseCd,
    replyYm,
    supplierNm,
    expense,
    branchCd,
    branchNm,
    factoryNm,
    supplierBranchCd,
    supplierBranchNm
  } = useAppSelector((state) => state.confirmreply.confirmReplyInfo)

  const replyStsCd = useAppSelector((state) => state.confirmreply.replyStsCd)
  const userProp = useAppSelector((state) => state.common.userProp)

  const [, resetLeaveGuard] = useLeaveGuard([isChanged], '内容が編集中です。このページを離れますか？')

  // 過去月の判定フラグ
  const date = new Date()
  const currentYear = date.getFullYear()
  const currentMonth = date.getMonth() + 1
  const simulatedYear = Number(simulationYm.substring(0, 4))
  const simulatedMonth = Number(simulationYm.substring(4, simulationYm.length))
  const lastMonthFlg = simulatedYear < currentYear || (simulatedYear === currentYear && simulatedMonth < currentMonth)

  // 過去月または(業者ユーザかつ回答済)または確認済または(支店ユーザかつ自支店以外)の場合、入力項目を非活性にする
  const nonCtrlFlg =
    lastMonthFlg ||
    (userProp?.authority === Authority.Supplier && replyStsCd === '5') ||
    replyStsCd === '7' ||
    (userProp?.authority === Authority.Branch && userProp?.branchCode !== branchCd)

  const handleGetHearingSheet = () => {
    dispatch(CommonActions.setIsLoading(true))
    HearingSheetItemGateway.getHearingSheetItem(
      simulationYm,
      supplierCd,
      factoryCd,
      expenseCd,
      supplierBranchCd
    )
      .then((res) => {
        if (!res.error) {
          const hearingSheetData: GetSupplierHearingSheetItemRes = res.data
          setHearingSheetVal(hearingSheetData)
        } else {
          if (Array.isArray(res.messages) && res.messages.length > 0) {
            const errorMessages = res.messages.map<ErrorMessage>((message) => {
              return {
                id: message.id,
                content: message.message
              }
            })
            setErrorMessages(errorMessages)
          }
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
      })
  }
  /**
   * 当社専従率
   */
  const handleNumericTextBoxVal = (event: NumericTextBoxChangeEvent) => {
    setHearingSheetVal(
      produce((draft) => {
        draft.ourCompanyOccupancy = event.value as number
      })
    )
    setIsChanged(true)
  }
  /**
   * 回答者名
   */
  const handleReplyNmFirstVal = (event: InputChangeEvent) => {
    setHearingSheetVal(
      produce((draft) => {
        draft.replyNmFirst = event.value
      })
    )
    setIsChanged(true)
  }
  /**
   * 回答者姓
   */
  const handleReplyNmLastVal = (event: InputChangeEvent) => {
    setHearingSheetVal(
      produce((draft) => {
        draft.replyNmLast = event.value
      })
    )
    setIsChanged(true)
  }
  /**
   * 市況動向
   */
  const handleMarketTrendVal = (event: TextAreaChangeEvent) => {
    setHearingSheetVal(
      produce((draft) => {
        draft.marketTrend = event.value
      })
    )
    setIsChanged(true)
  }
  /**
   * 山積み状況および工程変動情報
   */
  const handleYamadumiStsVal = (event: TextAreaChangeEvent) => {
    setHearingSheetVal(
      produce((draft) => {
        draft.yamadumiSts = event.value
      })
    )
    setIsChanged(true)
  }
  /**
   * 新規物件情報
   */
  const handleNewBuildingStsVal = (event: TextAreaChangeEvent) => {
    setHearingSheetVal(
      produce((draft) => {
        draft.newBuildingSts = event.value
      })
    )
    setIsChanged(true)
  }
  /**
   * 現状での問題点・その他
   */
  const handleProblemVal = (event: TextAreaChangeEvent) => {
    setHearingSheetVal(
      produce((draft) => {
        draft.problem = event.value
      })
    )
    setIsChanged(true)
  }

  useEffect(() => {
    // データストアチェック．業者ヒアリング遷移パラメータ．パラメータが設定されていない場合、エラーページへ遷移する
    const isPageError =
      regStatus === undefined ||
      regStatus === null ||
      simulationYm === '' ||
      supplierCd === '' ||
      factoryCd === '' ||
      expenseCd === '' ||
      replyYm === '' ||
      expense === '' ||
      supplierNm === '' ||
      branchCd === '' ||
      branchNm === '' ||
      supplierBranchCd === ''

    if (isPageError) {
      const pageParam = {
        regStatus,
        simulationYm,
        supplierCd,
        factoryCd,
        expenseCd,
        replyYm,
        expense,
        supplierNm,
        branchCd,
        branchNm,
        supplierBranchCd
      }
      throw new Error(`PageError Occurred. ${JSON.stringify(pageParam)}`)
    }
  }, [
    regStatus,
    simulationYm,
    supplierCd,
    factoryCd,
    expenseCd,
    replyYm,
    expense,
    supplierNm,
    branchCd,
    branchNm,
    supplierBranchCd
  ])

  useEffect(() => {
    handleGetHearingSheet()
  }, [])

  return (
    <div>
      <SupplierHearingHeader
        regStatus={hearingSheetVal?.addStsCd}
        promptInfo={hearingMessage.hearingSheet.headerPromptInfo}
        supplierHeaderInfo={{
          replyYm,
          supplierNm,
          expense,
          branchNm,
          factoryNm,
          supplierBranchNm
        }}
      />

      <div style={{ margin: 20 }}>
        <StackLayout gap={10} orientation="vertical">
          <StackLayout orientation="horizontal" style={{ width: 450 }}>
            <div>
              <Label style={{ fontWeight: 'bold' }}>回答者</Label>
              <StackLayout
                gap={5}
                orientation="horizontal"
                style={{ width: 200 }}
              >
                <Input
                  placeholder={'姓'}
                  value={hearingSheetVal?.replyNmLast}
                  onChange={handleReplyNmLastVal}
                  disabled={nonCtrlFlg}
                />
                <Input
                  placeholder={'名'}
                  value={hearingSheetVal?.replyNmFirst}
                  onChange={handleReplyNmFirstVal}
                  disabled={nonCtrlFlg}
                />
              </StackLayout>
            </div>

            <div>
              <Label style={{ fontWeight: 'bold' }}>当社専従率</Label>
              <StackLayout
                gap={5}
                orientation="horizontal"
                style={{ width: 200 }}
              >
                <NumericTextBox
                  value={hearingSheetVal?.ourCompanyOccupancy}
                  onChange={handleNumericTextBoxVal}
                  disabled={nonCtrlFlg}
                />
                <span>%</span>
              </StackLayout>
            </div>
          </StackLayout>
          <div style={{ display: 'flex' }}>
            <Label style={{ fontWeight: 'bold' }}>１．市況動向</Label>{' '}
            <Typography.p
              style={{ marginLeft: '10.5%' }}
              fontWeight={'bold'}
              themeColor={'error'}
              fontSize={'large'}
            >
              {hearingMessage.hearingSheet.marketTrendAnnotationInfo}
            </Typography.p>
          </div>

          <TextArea
            rows={4}
            value={hearingSheetVal?.marketTrend}
            onChange={handleMarketTrendVal}
            disabled={nonCtrlFlg}
          />
          <div style={{ display: 'flex' }}>
            <Label style={{ fontWeight: 'bold' }}>
              ２．山積み状況および工程変動情報
            </Label>
            <Typography.p
              style={{ marginLeft: '2%' }}
              fontWeight={'bold'}
              themeColor={'error'}
              fontSize={'large'}
            >
              {hearingMessage.hearingSheet.yamadumiStsAnnotationInfo}
            </Typography.p>
          </div>
          <TextArea
            rows={4}
            value={hearingSheetVal?.yamadumiSts}
            onChange={handleYamadumiStsVal}
            disabled={nonCtrlFlg}
          />
          <div style={{ display: 'flex' }}>
            <Label style={{ fontWeight: 'bold' }}>３．新規物件情報</Label>
            <Typography.p
              style={{ marginLeft: '9%' }}
              fontWeight={'bold'}
              themeColor={'error'}
              fontSize={'large'}
            >
              {hearingMessage.hearingSheet.newBuildingStsAnnotationInfo}
            </Typography.p>
          </div>
          <TextArea
            rows={4}
            value={hearingSheetVal?.newBuildingSts}
            onChange={handleNewBuildingStsVal}
            disabled={nonCtrlFlg}
          />
          <div style={{ display: 'flex' }}>
            <Label style={{ fontWeight: 'bold' }}>
              ４．現状での問題点・その他
            </Label>
            <Typography.p
              style={{ marginLeft: '5%' }}
              fontWeight={'bold'}
              themeColor={'error'}
              fontSize={'large'}
            >
              {hearingMessage.hearingSheet.problemAnnotationInfo}
            </Typography.p>
          </div>
          <TextArea
            rows={4}
            value={hearingSheetVal?.problem}
            onChange={handleProblemVal}
            disabled={nonCtrlFlg}
          />
        </StackLayout>
      </div>

      <HearingSheetFooter
        hearingSheetVal={hearingSheetVal}
        nonCtrlFlg={nonCtrlFlg}
        handleGetHearingSheet={handleGetHearingSheet}
        setNotificationMessages={setNotificationMessages}
        setErrorMessages={setErrorMessages}
        isChanged
        resetLeaveGuard={resetLeaveGuard}
      />
      <ErrorMessages type="notification" messages={notificationMessages} />
      <ErrorMessages type="dialog" messages={errorMessages} />
    </div>
  )
}
